import { initializeApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  OAuthProvider,
} from 'firebase/auth';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { until, useStorage } from '@vueuse/core';
import { useApiClient } from '@/api/apiClient';
import type { OrganizationDto, UserDto } from '@/api/generated';
import { useToast } from 'primevue/usetoast';

const { api } = useApiClient();

const firebaseConfig = {
  apiKey: 'AIzaSyCOpx6gIJtCI2UrcBzbCgkOjswy-zamsDw',
  authDomain: 'phottr-prod-eu.firebaseapp.com',
  projectId: 'phottr-prod-eu',
  storageBucket: 'phottr-prod-eu.appspot.com',
  messagingSenderId: '1064099188700',
  appId: '1:1064099188700:web:4bcde7ccd1296379e88919',
};

// const firebaseConfig =
//   import.meta.env.VITE_PROJECT_ID === 'phottr-prod-eu'
//     ? {
//         apiKey: 'AIzaSyCOpx6gIJtCI2UrcBzbCgkOjswy-zamsDw',
//         authDomain: 'phottr-prod-eu.firebaseapp.com',
//         projectId: 'phottr-prod-eu',
//         storageBucket: 'phottr-prod-eu.appspot.com',
//         messagingSenderId: '1064099188700',
//         appId: '1:1064099188700:web:4bcde7ccd1296379e88919',
//       }
//     : {
//         apiKey: 'AIzaSyDQLFKNtCRGA5TH_w9FNq3fiK_Xu7CFXUc',
//         authDomain: 'phottr-dev-eu.firebaseapp.com',
//         projectId: 'phottr-dev-eu',
//         storageBucket: 'phottr-dev-eu.appspot.com',
//         messagingSenderId: '1086495903011',
//         appId: '1:1086495903011:web:865aaf95f3a4c9fa7fd096',
//       };

initializeApp(firebaseConfig);
const googleAuthProvider = new GoogleAuthProvider();
const appleAuthProvider = new OAuthProvider('apple.com');
appleAuthProvider.addScope('email');
appleAuthProvider.addScope('name');

const auth = getAuth();

const isReady = ref(false);
const isAuthenticated = ref(false);
const user = ref<UserDto>();
const organization = ref<OrganizationDto>();

onAuthStateChanged(auth, async user_ => {
  try {
    isReady.value = false;
    if (user_) {
      const res = await api.authControllerLogin({
        body: {
          authToken: await user_.getIdToken(),
        },
      });

      if (res.error) {
        isAuthenticated.value = false;
        user.value = undefined;
        organization.value = undefined;
        const toast = useToast();
        toast.add({ severity: 'error', summary: 'Failed to login.' });
        throw new Error(`Failed to login: ${res.error}`);
      }

      const data = res.data;
      if (!data) {
        isAuthenticated.value = false;
        user.value = undefined;
        organization.value = undefined;
        const toast = useToast();
        toast.add({ severity: 'error', summary: 'Failed to login.' });
        throw new Error('Failed to login, no data returned from login');
      }

      isAuthenticated.value = Boolean(data.user);
      user.value = {
        ...data.user,
        displayName: user_.displayName ?? '',
        photoURL: user_.photoURL ?? '',
      };
      organization.value = data.organization;
    } else {
      isAuthenticated.value = false;
      user.value = undefined;
      organization.value = undefined;
    }
  } catch (err) {
    isAuthenticated.value = false;
    user.value = undefined;
    organization.value = undefined;
    throw err;
  } finally {
    isReady.value = true;
  }
});

export function useAuth() {
  const router = useRouter();

  const impersonateUserId = useStorage('auth.impersonate', '');

  async function waitForReady() {
    await until(isReady).toBe(true);
  }

  async function signInWithGoogle() {
    // TODO: handle auth errors
    await signInWithPopup(auth, googleAuthProvider);
    await waitForReady();
    await router.replace('/products');
  }

  async function signInWithApple() {
    await signInWithPopup(auth, appleAuthProvider);
    await waitForReady();
    await router.replace('/products');
  }

  async function logout() {
    await api.authControllerLogout();
    await signOut(auth);
    isAuthenticated.value = false;
    user.value = undefined;
    await router.push('/login');
  }

  function getAccessToken() {
    return auth.currentUser?.getIdToken();
  }

  async function impersonate(userId: string) {
    if (!user.value?.superAdmin) {
      throw new Error('Only super admins can impersonate other users');
    }
    localStorage.setItem('auth.impersonate', userId);
    await router.replace('/products');
    location.reload();
  }

  async function stopImpersonating() {
    localStorage.removeItem('auth.impersonate');
    await api.authControllerStopImpersonating();
    await router.replace('/products');
    location.reload();
  }

  return {
    waitForReady,
    isAuthenticated,
    signInWithGoogle,
    signInWithApple,
    getAccessToken,
    user,
    organization,
    logout,
    impersonate,
    stopImpersonating,
    impersonateUserId,
  };
}
